import React from 'react'
import styled from 'styled-components'
import H1 from './H1'
import { MAIN_ID } from './SkipLink'

interface PageLayoutProps {
  /**
   * Titolo della pagina: diventa l'<h1>. Una pagina ha esattamente un <h1> (WCAG 1.3.1).
   *
   * Omettilo SOLO quando il contenuto fornisce già il proprio unico <h1> in una
   * posizione che non si può spostare — per esempio dentro una live region
   * `role="status"` (ThankYouSection) o dentro un box grafico sovrapposto
   * all'hero (ArticlePage). In quel caso garantire l'unicità dell'<h1> è
   * responsabilità del chiamante.
   */
  title?: string
  /** Se true l'<h1> resta nell'albero accessibile ma non è visibile (WCAG 1.3.1 + 2.4.6). */
  hideTitle?: boolean
  /** Passato al <main>: permette a styled(PageLayout) di stilarlo dall'esterno. */
  className?: string
  children: React.ReactNode
}

export default function PageLayout({
  title,
  hideTitle = false,
  className,
  children,
}: PageLayoutProps) {
  // Lo skip link NON sta qui: dev'essere il primo elemento focusabile del
  // documento, prima dell'header. Vive in App.tsx (vedi SkipLink.tsx).
  return (
    <>
      <Main id={MAIN_ID} tabIndex={-1} className={className}>
        {title !== undefined &&
          (hideTitle ? <VisuallyHiddenH1>{title}</VisuallyHiddenH1> : <H1>{title}</H1>)}
        {children}
      </Main>
    </>
  )
}

const Main = styled.main`
  /* L'header è position: fixed: senza questo il focus finisce sotto la barra (WCAG 2.4.11). */
  scroll-margin-top: 14rem;

  &:focus {
    outline: none;
  }
`

// clip + width/height 1px: il titolo resta leggibile da screen reader.
// display:none o visibility:hidden lo rimuoverebbero dall'albero accessibile,
// annullando il fix (vedi documentazione della remediation a11y, fuori dal repo — D3).
const VisuallyHiddenH1 = styled(H1)`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
